import React, { useCallback, useMemo } from 'react'
import { AsyncSelect, Flex } from '@workwhile/ui'
import { FullOptions, MatchData, Searcher } from 'fast-fuzzy'
import { FieldPath, useController, useFormContext } from 'react-hook-form'
import { useCompanyDetailsByIdQuery } from 'queries/company'
import { ShiftsTableSchema } from 'pages/Shifts/BulkCreateShifts/bulkCreateShiftsSchema'
import { ErrorMessage } from './ErrorMessage'

type PositionOption = { label: string; value: number }

const fuzzyOptions: FullOptions<PositionOption> = {
  threshold: 0.7,
  ignoreCase: true,
  ignoreSymbols: true,
  normalizeWhitespace: true,
  returnMatchData: false,
  keySelector: (obj) => [obj.label, obj.value.toString()],
}

export const PositionSelector = ({
  fieldName,
  companyId,
}: {
  fieldName: FieldPath<ShiftsTableSchema>
  companyId?: number | null
}) => {
  const { control } = useFormContext<ShiftsTableSchema>()

  const { field, fieldState } = useController({
    control,
    name: fieldName,
  })

  const { data: companyDetail } = useCompanyDetailsByIdQuery(companyId)

  const positionsOptions = useMemo(() => {
    return (
      companyDetail?.positions?.map((position) => ({
        label: `${position.name} (${position.id})`,
        value: position.id,
      })) ?? []
    )
  }, [companyDetail])

  const searcher = useMemo(() => {
    return new Searcher<PositionOption, FullOptions<PositionOption>>(
      positionsOptions,
      fuzzyOptions
    )
  }, [positionsOptions])

  const loadOptions = useCallback(
    (rawInput: string) => {
      if (!rawInput) return true

      const results = searcher.search(
        rawInput,
        fuzzyOptions
      ) as unknown as MatchData<PositionOption>[]

      return new Promise((resolve) => {
        resolve(results)
      })
    },
    [searcher]
  )

  return (
    <Flex flexDirection="column" gap={1}>
      <AsyncSelect
        block
        {...field}
        isClearable
        isSearchable
        placeholder={
          companyDetail ? 'Select a position' : 'Select a company first'
        }
        defaultOptions={positionsOptions}
        loadOptions={loadOptions}
        menuPortalTarget={document.body}
        value={positionsOptions.find((option) => option.value === field.value)}
        onChange={(option) => {
          field.onChange(option?.value)
        }}
        styles={{
          container: (base) => ({
            ...base,
            width: '200px',
          }),
        }}
      />
      <ErrorMessage error={fieldState.error?.message} />
    </Flex>
  )
}
